import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { Schedule } from '@mui/icons-material';
import {
  Box,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { isFuture } from 'date-fns';
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { FavoriteRowButton } from 'src/components/FavoriteRowButton';
import { ConvocationSummary } from 'src/types/convocation-summary';
import { Favorite } from 'src/types/favorite';
import { formatDate } from 'src/utils/date';

import { TimelineMobileView } from './MobileView';
import { SummaryCard } from '../SummaryCard';
import { FilterState } from '../index';

interface TimelineTableProps {
  timelineFilters: FilterState;
  sorting: MRT_SortingState;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
  setSorting: Dispatch<SetStateAction<MRT_SortingState>>;
  data: ConvocationSummary[];
  meta: { pagination: { total: number } };
  isError: boolean;
  isLoading: boolean;
}

const assembleiaTypes = [
  { value: 'AGE', label: 'Assembleia Extraordinária' },
  { value: 'AGO', label: 'Assembleia Ordinária' },
  { value: 'AGO/E', label: 'Assembleia Especial' },
];

export function TimelineTable({
  timelineFilters,
  sorting,
  pagination,
  setPagination,
  setSorting,
  data,
  meta,
  isError,
  isLoading,
}: TimelineTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );

  const queryClient = useQueryClient();

  const handleAddFavorite = (rowId: number, favorite: Favorite) => {
    queryClient.invalidateQueries({
      queryKey: ['timeline-data', timelineFilters, sorting, pagination],
    });
  };

  const handleRemoveFavorite = (rowId: number) => {
    queryClient.invalidateQueries({
      queryKey: ['timeline-data', timelineFilters, sorting, pagination],
    });
  };

  const columns = useMemo<MRT_ColumnDef<ConvocationSummary>[]>(
    () => [
      {
        accessorKey: 'datareferencia',
        header: 'Data',
        size: 120,
        Cell: ({ cell }) => {
          const date = cell.getValue() as string;
          return isFuture(new Date(date)) ? (
            <Tooltip title="Evento futuro" placement="top">
              <Box display="flex" alignItems="center" gap={1}>
                {formatDate(date)}
                <Schedule color="primary" fontSize="small" />
              </Box>
            </Tooltip>
          ) : (
            formatDate(date)
          );
        },
      },
      {
        accessorKey: 'operation.name',
        header: 'Operação',
        size: 200,
        Cell: ({ cell, row }) => {
          const { hashId, slug } = row.original.operation;
          const { slug: marketSlug } = row.original.market;
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <FavoriteRowButton
                type="operation"
                rowId={Number(hashId)}
                favorite={row.original.favorite as Favorite}
                handleAddFavorite={handleAddFavorite}
                handleRemoveFavorite={handleRemoveFavorite}
              />
              <Link
                to={`/operacao/${marketSlug}/${slug}/${hashId}`}
                style={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  textDecoration: 'underline',
                }}>
                {cell.getValue<string>()}
              </Link>
            </Box>
          );
        },
      },
      {
        accessorKey: 'categoriadocumento',
        header: 'Categoria',
        size: 160,
      },
      {
        accessorKey: 'tipodocumento',
        header: 'Tipo',
        size: 210,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          return (
            <Typography variant="body2">
              {assembleiaTypes.find((type) => type.value === value)?.label}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'especiedocumento',
        header: 'Espécie',
        size: 200,
      },
      {
        accessorKey: 'market.name',
        header: 'Mercado',
        size: 100,
      },
    ],
    [theme, handleAddFavorite, handleRemoveFavorite],
  );

  const table = useMaterialReactTable({
    columns,
    data,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: (updaterOrValue) => {
      setPagination(updaterOrValue as MRT_PaginationState);
    },
    onSortingChange: (updaterOrValue) => {
      setSorting(updaterOrValue as MRT_SortingState);
    },
    enableExpanding: true,
    enableExpandAll: false,
    renderDetailPanel: ({ row }) => (
      <Box sx={{ p: 2 }}>
        <SummaryCard key={row.original.id} convocation={row.original} />
      </Box>
    ),
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      density: 'comfortable',
    },
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      showRowsPerPage: true,
      variant: 'outlined',
    },
    paginationDisplayMode: 'pages',
    muiToolbarAlertBannerProps: isError
      ? {
          color: 'error',
          children: 'Erro ao carregar dados',
        }
      : undefined,
    rowCount: meta?.pagination.total ?? 0,
    state: {
      columnFilters,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isLoading,
      sorting,
    },
    localization: {
      ...MRT_Localization_PT_BR,
      noRecordsToDisplay: 'Nenhum resultado encontrado',
    },
    enableFullScreenToggle: false,
    enableEditing: false,
    enableHiding: false,
    enableColumnDragging: false,
    enableDensityToggle: false,
    enableStickyHeader: true,
    enableColumnActions: false,
    enableGlobalFilter: false,
    enableFilters: false,
    enableTopToolbar: false,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        row.toggleExpanded();
      },
      sx: {
        cursor: 'pointer',
      },
    }),
    muiTableBodyCellProps: ({ cell, row }) => ({
      sx: (theme) => ({
        '&:hover': {
          backgroundColor: row.getIsExpanded()
            ? 'transparent'
            : theme.palette.grey[200],
        },
      }),
    }),
  });

  if (isMobile) {
    return (
      <TimelineMobileView
        data={data}
        isLoading={isLoading}
        isError={isError}
        pagination={pagination}
        setPagination={setPagination}
        totalCount={meta?.pagination.total ?? 0}
        handleAddFavorite={handleAddFavorite}
        handleRemoveFavorite={handleRemoveFavorite}
      />
    );
  }

  return (
    <Box>
      <MaterialReactTable table={table} />
    </Box>
  );
}
